import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../components/AuthProvider';
import { AccessDeniedPage } from '../components/AdminRoute';
import { cn } from '../lib/utils';
import { apiFetch } from '../lib/api';
import { useApiJson } from '../hooks/useApi';
import { EventsManagement } from '../components/events/EventsManagement';
import { QuizCampaignsManagement } from '../components/quiz/QuizCampaignsManagement';
import type { BannerConfig } from '../types';
import { toDatetimeLocalValue } from '../lib/format-datetime';
import { MarkdownField } from '../components/markdown/MarkdownField';
import { LeadershipManagement } from '../components/admin/LeadershipManagement';
import { AboutUsManagement } from '../components/admin/AboutUsManagement';
import { GalleryManagement } from '../components/admin/GalleryManagement';
import { PagesManagement } from '../components/admin/PagesManagement';
import { AdminTabs } from '../components/admin/AdminTabs';
import type { AdminContentTab } from '../lib/admin-tab-order';

interface BannerDto {
  ok: boolean;
  banner: BannerConfig;
}

export function AdminPage() {
  const { canAccessAdminRoutes, isOrganizer } = useAuth();
  const organizerOnly = isOrganizer && !canAccessAdminRoutes;
  const [activeTab, setActiveTab] = useState<AdminContentTab>('events');

  if (!canAccessAdminRoutes && !isOrganizer) {
    return (
      <Layout>
        <AccessDeniedPage />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
            {organizerOnly ? 'Events' : 'Admin'}
          </h1>
          <p className="text-[var(--color-text-secondary)] text-sm mt-1">
            {organizerOnly
              ? 'View events and registrations. Score the tournaments assigned to organizers.'
              : 'Manage events, website content and the gallery.'}
          </p>
        </header>

        {organizerOnly ? (
          <EventsManagement />
        ) : (
        <AdminTabs activeTab={activeTab} onSelect={setActiveTab}>
        <div>
          {activeTab === 'events' && <EventsManagement />}
          {activeTab === 'gallery' && <GalleryManagement />}
          {activeTab === 'pages' && <PagesManagement />}
          {activeTab === 'quizzes' && <QuizCampaignsManagement />}
          {activeTab === 'banner' && <BannerManagement />}
          {activeTab === 'about' && <AboutUsManagement />}
          {activeTab === 'leadership' && <LeadershipManagement />}
        </div>
        </AdminTabs>
        )}
      </div>
    </Layout>
  );
}

function BannerManagement() {
  const { data } = useApiJson<BannerDto>('/api/settings/dashboardBanner');
  const [config, setConfig] = useState<BannerConfig>({
    title: 'Mandal Vision 2026',
    description:
      'Strengthening our bonds through spiritual, social, and professional growth activities for the Maheshwari community in Chennai.',
    linkUrl: 'https://smymchennai.in/',
    linkText: 'Learn More',
    countdownText: '',
    countdownEndsAt: '',
    visible: true,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.banner) setConfig(data.banner);
  }, [data]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiFetch('/api/settings/dashboardBanner', {
        method: 'PUT',
        body: JSON.stringify(config),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  return (
    <div className="bg-[var(--color-surface-1)] p-6 rounded-[var(--radius-lg)] border border-[var(--color-border)] space-y-5">
      <div>
        <h3 className="text-base font-semibold text-[var(--color-text-primary)]">Dashboard Banner</h3>
        <p className="text-[var(--color-text-secondary)] text-sm mt-0.5">Configure the featured section shown on the home dashboard.</p>
      </div>

      <form onSubmit={handleSave} className="space-y-4 max-w-2xl">
        <label className="inline-flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={config.visible !== false}
            onChange={(ev) => setConfig({ ...config, visible: ev.target.checked })}
            className="h-5 w-5 rounded border-[var(--color-border)] accent-[var(--color-accent)]"
          />
          <span className="text-sm font-medium text-[var(--color-text-secondary)]">
            Show banner on dashboard
          </span>
        </label>
        <div>
          <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">Title</label>
          <input
            type="text"
            className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-3 px-4 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent-subtle)] transition-all duration-[var(--duration-fast)] text-sm font-medium"
            value={config.title}
            onChange={(ev) => setConfig({ ...config, title: ev.target.value })}
            required
          />
        </div>
        <MarkdownField
          label="Description"
          hint="Markdown supported. Line breaks are kept on the dashboard banner."
          rows={4}
          value={config.description}
          onChange={(description) => setConfig({ ...config, description })}
          required
        />
        <div>
          <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">Link URL</label>
          <input
            type="url"
            className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-3 px-4 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent-subtle)] transition-all duration-[var(--duration-fast)] text-sm"
            value={config.linkUrl}
            onChange={(ev) => setConfig({ ...config, linkUrl: ev.target.value })}
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">Button text</label>
          <input
            type="text"
            placeholder="Learn More"
            className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-3 px-4 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent-subtle)] transition-all duration-[var(--duration-fast)] text-sm"
            value={config.linkText ?? ''}
            onChange={(ev) => setConfig({ ...config, linkText: ev.target.value })}
            required
          />
        </div>

        <div className="pt-4 border-t border-[var(--color-border)] space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">Countdown (optional)</h4>
            <p className="text-[var(--color-text-secondary)] text-xs mt-0.5">
              Add a live timer on the home banner. Leave the date empty to hide it.
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">
              Countdown text
            </label>
            <input
              type="text"
              placeholder="e.g. Annual meet begins in"
              className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-3 px-4 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent-subtle)] transition-all duration-[var(--duration-fast)] text-sm"
              value={config.countdownText ?? ''}
              onChange={(ev) => setConfig({ ...config, countdownText: ev.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">
              Countdown date &amp; time
            </label>
            <input
              type="datetime-local"
              className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-3 px-4 text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent-subtle)] transition-all duration-[var(--duration-fast)] text-sm"
              value={toDatetimeLocalValue(config.countdownEndsAt ?? '')}
              onChange={(ev) =>
                setConfig({
                  ...config,
                  countdownEndsAt: ev.target.value ? new Date(ev.target.value).toISOString() : '',
                })
              }
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white font-medium py-2.5 px-5 rounded-[var(--radius-md)] transition-colors duration-[var(--duration-fast)] text-sm flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? (
            'Saving...'
          ) : saved ? (
            <>
              <Check className="w-4 h-4" /> Saved
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </form>
    </div>
  );
}
